import { useEffect, useRef, useState } from "react";
import { useLang } from "../OneHealthContext";
import { StatusIcon, VerdictBadge, ScoreRing } from "../components";
import { PARAM_META, scoreStatus, alertnessScore, combineVerdict, VERDICT } from "../config";
import { generateReportPdf } from "../report/pdf";
import { getDeviceKey, submitResults } from "../roster";

/* RESULT — the fit-for-work verdict + the full readiness report.
   The verdict, BP band and every risk status arrive from the server in a
   SANITIZED report (no raw BP mmHg, no thresholds). RAG colour + icon carry the
   verdict (green tick / amber warning / red cross); the detailed report lists
   each parameter with its result, reference range and status. */
const VERDICT_STATUS = { [VERDICT.CLEAR]: "green", [VERDICT.CAUTION]: "amber", [VERDICT.REFER]: "red" };

export default function Result({
  result,
  pulmoResult,
  pvtResult,
  onRestart,
  siteJourney = false,
  deferSubmission = false,
  onWellbeingComplete,
}) {
  const { tr } = useLang();
  const [downloading, setDownloading] = useState(false);
  const [feelingChoice, setFeelingChoice] = useState(null);
  const [wellbeingOutcome, setWellbeingOutcome] = useState(null);
  const [ohcRequestedAt, setOhcRequestedAt] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(false);
  const [syncAttempt, setSyncAttempt] = useState(0);

  const report = result && result.ok ? result : { verdict: VERDICT.CLEAR, wellbeing: null, params: [] };
  const employee = result?.employee || {};

  const alertness = pvtResult ? alertnessScore(pvtResult, employee.age) : null;

  // Overall verdict factors ALL assessments done, weighted face > pulmo > alertness.
  const verdict = combineVerdict({
    faceVerdict: report.verdict || VERDICT.CLEAR,
    pulmoScore: pulmoResult && pulmoResult.score != null ? pulmoResult.score : null,
    alertness,
  });
  const vStatus = VERDICT_STATUS[verdict] || "green";

  const scores = [
    { key: "wellbeing", label: tr("result.wellbeing"), value: report.wellbeing },
    { key: "pulmonary", label: tr("result.pulmonary"), value: pulmoResult && pulmoResult.score != null ? pulmoResult.score : null },
    { key: "alertness", label: tr("result.alertness"), value: alertness },
  ];

  // Detailed report rows from the sanitized server params.
  const rows = (report.params || []).map((p) => {
    const meta = PARAM_META[p.key] || {};
    const label = meta.labelKey ? tr(meta.labelKey) : p.key;
    let value, unit;
    if (meta.band) {
      const k = meta.bandLabelKey && meta.bandLabelKey[p.band];
      value = k ? tr(k) : String(p.band || "—");
      // Server-provided band range (e.g. BP): "Normal (115–125 / 75–80)".
      if (p.bandRange) value = `${value} (${p.bandRange})`;
      unit = "";
    } else {
      value = p.value;
      unit = meta.unit || "";
    }
    return { key: p.key, label, value, unit, range: meta.range, status: p.status || "na" };
  });

  /* Headline risks, shown here and sent to the dashboard as hypertensionRisk /
     cardiacRisk. Both are derived from the banded scan output — the BP band
     rather than raw mmHg — so what is displayed matches what is submitted. */
  const bpParam = (report.params || []).find((p) => p.key === "bp");

  /* The BP parameter comes back as a wellbeing-style band ("good", "excellent",
     "fair"…), which is meaningless as a RISK label — a risk is low, medium or
     high. Map it once, here, so the screen and the API payload carry the same
     three-level scale. */
  const RISK_FROM_BAND = {
    excellent: "low", good: "low", normal: "low", optimal: "low",
    average: "medium", fair: "medium", elevated: "medium", moderate: "medium",
    poor: "high", high: "high", severe: "high", refer: "high",
  };
  // Whitelisted: an unrecognised upstream band yields NO risk chip / payload
  // field rather than a value outside the dashboard's low|medium|high enum.
  const hypertensionRisk = bpParam?.band
    ? (RISK_FROM_BAND[String(bpParam.band).toLowerCase()] || null)
    : null;
  const cardiacRisk =
    (report.verdict === "refer") ? "high"
    : (report.verdict === "caution") ? "moderate"
    : (bpParam || (report.params || []).some((p) => p.key === "hr")) ? "low"
    : null;

  const RISK_TONE = { low: "green", medium: "amber", moderate: "amber", high: "red" };

  /* ── Dashboard submission ──
     Workers identified against the org roster (workerId present) get their
     screening pushed to the dashboard once; unregistered manual entries are
     kiosk-local only. RAG statuses/bands only — raw BP never leaves the flow. */
  const submittedRef = useRef(false);
  const [synced, setSynced] = useState(false);
  useEffect(() => {
    const wid = employee.workerId;
    // Site mode (no device key) is read-only roster access: results are never
    // submitted without the device credential.
    if (!wid || !result?.ok || submittedRef.current || !getDeviceKey()) return;
    if (deferSubmission && !wellbeingOutcome) return;
    submittedRef.current = true;
    setSyncing(true);
    setSyncError(false);
    const vMap = { [VERDICT.CLEAR]: "fit", [VERDICT.CAUTION]: "conditional", [VERDICT.REFER]: "not_fit" };
    const statusWord = (s) => (s === "green" ? "pass" : s === "amber" ? "warning" : s === "red" ? "fail" : "pass");
    // Contract: scores are 0–1000 — send only finite, clamped values.
    const score = (v) => (Number.isFinite(Number(v)) ? Math.max(0, Math.min(1000, Math.round(Number(v)))) : undefined);
    const results = [{
      assessment: "face_scan",
      status: report.verdict === VERDICT.REFER ? "fail" : report.verdict === VERDICT.CAUTION ? "warning" : "pass",
      value: report.wellbeing != null ? String(report.wellbeing) : "",
      detail: "Contactless rPPG vitals scan",
    }];
    if (pulmoResult && pulmoResult.score != null) results.push({
      assessment: "pulmo_scan", status: statusWord(scoreStatus(pulmoResult.score)),
      value: String(pulmoResult.score), detail: "Lung health screening",
    });
    else if (pulmoResult?.mode === "cough_only" && pulmoResult?.cough) results.push({
      assessment: "pulmo_scan", status: "pass",
      value: "cough_collected", detail: "Cough-only sample collected; no clinical Pulmo score generated",
    });
    if (alertness != null) results.push({
      assessment: "attention", status: statusWord(scoreStatus(alertness)),
      value: String(alertness), detail: "PVT-B attention test",
    });
    const metrics = (report.params || []).slice(0, 100).map((p) => ({
      key: String(p.key).slice(0, 64),
      value: String(p.band ?? p.value ?? "").slice(0, 500),
      unit: (PARAM_META[p.key]?.unit || "").slice(0, 32),
      status: String(p.status || "na").slice(0, 16),
    }));
    if (wellbeingOutcome === "ohc_requested" && ohcRequestedAt) metrics.push({
      key: "virtual_ohc_requested_at",
      value: ohcRequestedAt,
      unit: "",
      status: "warn",
    });
    submitResults({
      workerId: wid,
      verdict: vMap[verdict] || "fit",
      screenedAt: new Date().toISOString(),
      wellbeingScore: score(report.wellbeing),
      pulmoScore: score(pulmoResult?.score),
      attentionIndex: score(alertness),
      // Same derivation as the on-screen risk chips: BP band → hypertension,
      // face verdict → cardiac. Screen and payload can never disagree.
      hypertensionRisk: hypertensionRisk || undefined,
      cardiacRisk: cardiacRisk === "moderate" ? "medium" : (cardiacRisk || undefined),
      results, metrics,
    })
      .then(() => {
        setSynced(true);
        setSyncing(false);
        onWellbeingComplete?.(wellbeingOutcome);
      })
      .catch((e) => {
        console.warn("dashboard result submit failed:", e?.message);
        submittedRef.current = false;
        setSyncing(false);
        setSyncError(true);
      });
  }, [wellbeingOutcome, syncAttempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const notes = [tr("result.logged"), tr("result.baselineUpdated")];
  if (synced) notes.push(siteJourney ? tr("siteJourney.dashboardSynced") : "Result synced to the organisation dashboard");
  if (verdict === VERDICT.CAUTION) notes.push(tr("result.supervisor"));
  if (verdict === VERDICT.REFER) notes.push(tr("result.ohc"));

  const download = async () => {
    setDownloading(true);
    try {
      await generateReportPdf({ report, verdict, pulmo: pulmoResult, pvt: pvtResult, alertness, employee, tr });
    } catch (e) {
      console.error("PDF generation failed", e);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="oh-view oh-result">
      {/* ── Verdict ── */}
      <div className="oh-result-head">
        <div className="oh-kicker">{tr("result.badge")}</div>
        <VerdictBadge status={vStatus} />
        <div className={`oh-verdict-label ${vStatus}`}>{tr(`result.${verdict}Label`)}</div>
        <h2 className="oh-h2">{tr(`result.${verdict}Title`)}</h2>
        <p className="oh-sub" style={{ maxWidth: 360 }}>{tr(`result.${verdict}Body`)}</p>
      </div>

      {/* ── Detailed report ── */}
      {rows.length > 0 && (
        <>
          <div className="oh-kicker oh-section">{tr("result.reportTitle")}</div>
          <div className="oh-rtable" role="table">
            <div className="oh-rrow oh-rhead" role="row">
              <span>{tr("result.colParam")}</span>
              <span>{tr("result.colResult")}</span>
              <span className="oh-rrange">{tr("result.colRange")}</span>
              <span className="oh-rstat">{tr("result.colStatus")}</span>
            </div>
            {rows.map((r) => (
              <div className="oh-rrow" role="row" key={r.key}>
                <span className="oh-rname">{r.label}</span>
                <span className="oh-rval oh-mono">
                  {r.value}{r.unit ? <small>{r.unit}</small> : null}
                </span>
                <span className="oh-rrange oh-mono">{r.range || "—"}</span>
                <span className="oh-rstat"><StatusIcon status={r.status} size={20} /></span>
              </div>
            ))}
          </div>
          {rows.some((r) => r.key === "diabRisk") && (() => {
            // Note wording follows the worker's actual HbA1c band.
            const hba1c = (report.params || []).find((p) => p.key === "hba1c");
            const key = hba1c && hba1c.band === "aboveOptimal" ? "result.diabRiskNoteAbove" : "result.diabRiskNoteOptimal";
            return <p className="oh-foot" style={{ marginTop: 8 }}>{tr(key)}</p>;
          })()}
        </>
      )}

      {/* ── Lung screening ── */}
      {pulmoResult && (
        <>
          <div className="oh-kicker oh-section">{tr("result.pulmonary")}</div>
          {pulmoResult.mode === "cough_only" ? (
            <div className="oh-rtable">
              <div className="oh-rrow oh-lung">
                <span className="oh-rname">{tr("result.coughSample")}</span>
                <span className="oh-rval"><StatusIcon status="green" size={18} /> {tr("result.collected")}</span>
              </div>
            </div>
          ) : (
            <div className="oh-rtable">
              <div className="oh-rrow oh-lung">
                <span className="oh-rname">{tr("result.pFevFvc")}</span>
                <span className="oh-rval oh-mono">{pulmoResult.fevFvc != null ? pulmoResult.fevFvc : tr("result.unavailable")}</span>
              </div>
              <div className="oh-rrow oh-lung">
                <span className="oh-rname">{tr("result.pOad")}</span>
                <span className="oh-rval">
                  {pulmoResult.oad == null
                    ? <em className="oh-muted">{tr("result.unavailable")}</em>
                    : (<><StatusIcon status={pulmoResult.oad ? "red" : "green"} size={18} /> {pulmoResult.oad ? tr("result.oadTrue") : tr("result.oadFalse")}</>)}
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Alertness detail ── */}
      {pvtResult && (
        <>
          <div className="oh-kicker oh-section">{tr("result.attention")}</div>
          <div className="oh-rtable">
            <div className="oh-rrow oh-lung">
              <span className="oh-rname">{tr("result.attentionMean")}</span>
              <span className="oh-rval oh-mono">{pvtResult.mean}<small>{tr("result.uMs")}</small></span>
            </div>
            <div className="oh-rrow oh-lung">
              <span className="oh-rname">{tr("result.attentionLapses")}</span>
              <span className="oh-rval oh-mono">{pvtResult.lapses}</span>
            </div>
          </div>
          <p className="oh-foot" style={{ marginTop: 8 }}>{tr("result.attentionNote")}</p>
        </>
      )}

      {/* ── Headline scores — all three, shown at the end of the report ── */}
      <div className="oh-kicker oh-section">{tr("result.scoresTitle")}</div>
      <div className="oh-scores">
        {scores.map((s) => {
          const st = scoreStatus(s.value);
          return (
            <div className="oh-scard" key={s.key}>
              <ScoreRing value={s.value} status={st} />
              <div className="oh-scard-label">{s.label}</div>
              {s.value == null && <div className="oh-scard-na">{tr("result.na")}</div>}
            </div>
          );
        })}
      </div>

      {/* ── Notes ── */}
      <div className="oh-notes">
        {notes.map((n, i) => (
          <div className="oh-note" key={i}><span className="b" />{n}</div>
        ))}
      </div>

      {(hypertensionRisk || cardiacRisk) && (
        <div className="oh-risks">
          {hypertensionRisk && (
            <div className={`oh-risk ${RISK_TONE[hypertensionRisk] || "na"}`}>
              <span className="oh-risk-label">{tr("result.hypertensionRisk")}</span>
              <span className="oh-risk-value">{tr(`risk.${hypertensionRisk}`)}</span>
            </div>
          )}
          {cardiacRisk && (
            <div className={`oh-risk ${RISK_TONE[cardiacRisk] || "na"}`}>
              <span className="oh-risk-label">{tr("result.cardiacRisk")}</span>
              <span className="oh-risk-value">{tr(`risk.${cardiacRisk}`)}</span>
            </div>
          )}
        </div>
      )}

      <div className="oh-spacer" />

      {siteJourney && (
        <section className="oh-wellbeing-card" aria-live="polite">
          <div className="oh-kicker">{tr("siteJourney.wellbeingKicker")}</div>
          <h3>{tr("siteJourney.feelingQuestion")}</h3>
          {!feelingChoice && (
            <>
              {/* Product copy deliberately requires the callout “Not feeling
                  okay?” and a visible “No” action that opens OHC. The direct
                  question below removes the grammatical ambiguity: “No” now
                  clearly answers “Are you feeling okay right now?” */}
              <p className="oh-wellbeing-prompt" id="oh-feeling-prompt">{tr("siteJourney.feelingPrompt")}</p>
              <div className="oh-wellbeing-actions" role="group" aria-labelledby="oh-feeling-prompt">
                <button type="button" className="oh-btn ghost" onClick={() => setFeelingChoice("not_ok")}>
                  <span>{tr("siteJourney.notOkay")}</span>
                  <small>{tr("siteJourney.notOkayHint")}</small>
                </button>
                <button type="button" className="oh-btn" onClick={() => {
                  setFeelingChoice("fine");
                  setWellbeingOutcome("fine");
                }}>
                  {tr("siteJourney.imFine")}
                </button>
              </div>
            </>
          )}
          {feelingChoice === "not_ok" && !synced && (
            <div className="oh-ohc-panel">
              <h4>{tr("siteJourney.connectTitle")}</h4>
              <p>{tr("siteJourney.connectSub")}</p>
              <button type="button" className="oh-btn" disabled={syncing} onClick={() => {
                if (!ohcRequestedAt) setOhcRequestedAt(new Date().toISOString());
                setWellbeingOutcome("ohc_requested");
                if (wellbeingOutcome === "ohc_requested") setSyncAttempt((n) => n + 1);
              }}>
                {syncing ? tr("siteJourney.loggingRequest") : tr("siteJourney.callTeam")}
              </button>
            </div>
          )}
          {feelingChoice === "fine" && !synced && (
            <p className="oh-wellbeing-status">{syncing ? tr("siteJourney.savingResult") : tr("siteJourney.thankYou")}</p>
          )}
          {synced && (
            <div className="oh-ohc-confirm">
              <span className="oh-ohc-check" aria-hidden="true">✓</span>
              <div>
                <strong>{wellbeingOutcome === "ohc_requested" ? tr("siteJourney.requestLogged") : tr("siteJourney.resultSaved")}</strong>
                <p>{wellbeingOutcome === "ohc_requested" ? tr("siteJourney.requestLoggedSub") : tr("siteJourney.resultSavedSub")}</p>
              </div>
            </div>
          )}
          {syncError && !syncing && (
            <button type="button" className="oh-btn ghost oh-sync-retry" onClick={() => setSyncAttempt((n) => n + 1)}>
              {tr("siteJourney.retrySave")}
            </button>
          )}
        </section>
      )}

      <button className="oh-btn oh-download" onClick={download} disabled={downloading}>
        {downloading ? tr("result.preparing") : tr("result.download")}
      </button>
      <div style={{ height: 10 }} />
      {!siteJourney && verdict !== VERDICT.REFER && (
        <>
          <button className="oh-btn ghost" onClick={onRestart}>{tr("result.proceed")}</button>
          <div style={{ height: 10 }} />
        </>
      )}
      {(!siteJourney || synced) && <button className="oh-btn ghost" onClick={onRestart}>{tr(siteJourney ? "siteJourney.nextWorker" : "result.restart")}</button>}
      <div style={{ height: 14 }} />
      <div className="oh-foot">{tr("result.disclaimer")}</div>
    </div>
  );
}
